import React, { useEffect, useMemo, useRef, useState } from "react";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { connect } from "react-redux";
import Ajv from "ajv";

import actionCreator from "@modules/service/actionCreator";
import { removeNullAndUndefinedProps } from "@transformers/object";
import { Button, SchemaForm, SchemaTable, Text, View } from "@components";
import { getAbsenceMonitoringSchema, getAbsenceMonitoringDraft } from "@modules/service/selectors";

type Props = PropsFromLocalize;

import getUiSchema from "./appeals-ui-schema";

const DEFAULT_SCHEMA_TABLE_DATA = { id: null, name: "", isActive: true };

const Appeals = (props: Props) => {
  const { t, schema, draft } = props;
  const validatorRef = useRef(null);
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    kpiDays: 14,
  });
  const [reasonsData, setReasonsData] = useState([DEFAULT_SCHEMA_TABLE_DATA]);
  const [outcomeData, setOutcomeData] = useState([DEFAULT_SCHEMA_TABLE_DATA]);

  const uiSchema = getUiSchema({
    t,
    fileName: draft.appealConfig?.appealForm?.fileName,
    isActive: formData.isActive,
  });

  useEffect(() => {
    if (draft.content?.appeal?.id) {
      setFormData({ ...draft.content.appeal });
      setReasonsData(draft.content.appealReasons);
      setOutcomeData(draft.content.outcomeReasons);
    }
  }, []);

  useEffect(() => {
    if (schema && schema.properties) {
      const ajv = new Ajv();

      const stepSchema = {
        type: "object",
        required: ["appeal"],
        allOf: schema.allOf,
        properties: {
          appeal: schema.properties.appeal,
          appealReasons: schema.properties.appealReasons,
          outcomeReasons: schema.properties.outcomeReasons,
        },
      };

      validatorRef.current = ajv.compile(stepSchema);
    }
  }, [schema]);

  const handleChange = data => {
    setFormData(data);

    props.setDraft({
      ...draft,
      content: {
        ...draft.content,
        appeal: data,
      },
    });
  };

  const handleAddReason = () => {
    setReasonsData(prev => [...prev, DEFAULT_SCHEMA_TABLE_DATA]);
  };

  const handleAddOutcome = () => {
    setOutcomeData(prev => [...prev, DEFAULT_SCHEMA_TABLE_DATA]);
  };

  const handleChangeReasons = data => {
    setReasonsData(data);
    props.setDraft({
      ...draft,
      content: {
        ...draft.content,
        appealReasons: data,
      },
    });
  };

  const handleChangeOutcome = data => {
    setOutcomeData(data);
    props.setDraft({
      ...draft,
      content: {
        ...draft.content,
        outcomeReasons: data,
      },
    });
  };

  const isValid = () => {
    const validate = validatorRef.current;
    if (!validate) {
      return false;
    }

    const data =
      formData?.isActive === true
        ? { appeal: formData, appealReasons: reasonsData, outcomeReasons: outcomeData }
        : { appeal: { isActive: false, kpiDays: 14 } };

    const valid = validate(removeNullAndUndefinedProps(data));

    if (!valid) {
      console.warn("Appeals step validation errors:", validate.errors);
    }

    return valid;
  };

  const handleSubmit = () => {
    if (!draft || !draft.content) {
      return;
    }

    const { appealReasons, outcomeReasons, ...rest } = draft.content;
    const data =
      formData?.isActive === true
        ? {
            appeal: formData,
            appealReasons: reasonsData,
            outcomeReasons: outcomeData,
          }
        : { appeal: { isActive: false, kpiDays: 14 } };

    if (props.handleSubmit) {
      props.handleSubmit(data, true);
    }

    const prevData = formData?.isActive === true ? draft.content : rest;
    props.setDraft({
      ...draft,
      content: {
        ...prevData,
        ...data,
      },
    });
  };

  if (!props.editable || !schema) {
    return <div />;
  }

  return (
    <View padding="0 1.5rem" gap={8}>
      <Text className="message-box">{props.t("APPEALS_MESSAGE")}</Text>
      <SchemaForm
        ref={formRef}
        readonly={false}
        disabled={false}
        formData={formData}
        schema={schema.properties.appeal}
        uiSchema={uiSchema.appeal}
        onChange={handleChange}
        externalSubmit
        className="gs-container"
      />
      {Boolean(formData.isActive) && (
        <>
          <View className="gs-container">
            <View direction="row" justifyContent="space-between" alignItems="center">
              <Text type="h5">{t("REASON_FOR_APPEAL")}</Text>
              <Button materialIcon="add_circle" onClick={handleAddReason}>
                {t("ADD")}
              </Button>
            </View>
            <SchemaTable
              tableData={reasonsData}
              schema={schema.properties.appealReasons.items}
              uiSchema={uiSchema.reasons}
              onChange={handleChangeReasons}
              allowDeletes
            />
          </View>
          <View className="gs-container">
            <View direction="row" justifyContent="space-between" alignItems="center">
              <Text type="h5">{t("APPEAL_HEARING_OUTCOME")}</Text>
              <Button materialIcon="add_circle" onClick={handleAddOutcome}>
                {t("ADD")}
              </Button>
            </View>
            <SchemaTable
              tableData={outcomeData}
              schema={schema.properties.outcomeReasons.items}
              uiSchema={uiSchema.reasons}
              onChange={handleChangeOutcome}
              allowDeletes
            />
          </View>
        </>
      )}
      {props.editing ? null : (
        <View direction="row" justifyContent="end">
          <Button onClick={handleSubmit} disabled={!isValid()}>
            {props.t("ABSENCE_MONITORING_STEP5")}
          </Button>
        </View>
      )}
    </View>
  );
};

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    schema: getAbsenceMonitoringSchema(state),
    draft: getAbsenceMonitoringDraft(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setDraft: actionCreator.SetAbsenceMonitoringDraft,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(Appeals);
